import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, MessageSquare, ShoppingCart, Users, Check, CheckCheck, MessageCircle, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  ReactFlow,
  Background,
  Controls,
  Position,
  Handle,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

// Custom styles for React Flow edges
const edgeStyles = {
  strokeLinecap: 'round',
  stroke: '#334155',
  strokeWidth: 2,
};

// Sleek hexagonal glassmorphic node with subtle color based on type
const CustomNode = ({ data }: { data: any }) => {
  const { icon: IconComponent, color, label } = data;

  // Get gradient colors based on the color prop
  const getGradientColors = (color: string) => {
    switch (color) {
      case 'from-green-500 to-emerald-600':
        return 'from-green-500/15 via-emerald-500/10 to-teal-500/15';
      case 'from-blue-500 to-indigo-600':
        return 'from-blue-500/15 via-indigo-500/10 to-cyan-500/15';
      case 'from-purple-500 to-violet-600':
        return 'from-purple-500/15 via-violet-500/10 to-indigo-500/15';
      case 'from-slate-500 to-slate-600':
        return 'from-slate-500/15 via-slate-600/10 to-gray-500/15';
      case 'from-orange-500 to-red-600':
        return 'from-orange-500/15 via-red-500/10 to-pink-500/15';
      case 'from-cyan-500 to-blue-600':
        return 'from-cyan-500/15 via-blue-500/10 to-indigo-500/15';
      case 'from-pink-500 to-rose-600':
        return 'from-pink-500/15 via-rose-500/10 to-purple-500/15';
      default:
        return 'from-slate-500/15 via-slate-600/10 to-gray-500/15';
    }
  };

  const gradientClass = getGradientColors(color);

  return (
    <div className="flex flex-col items-center">
      {/* Node container with handles */}
      <div className="relative">
        {/* Connection handles */}
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          style={{ background: 'transparent', border: 'none' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ background: 'transparent', border: 'none' }}
        />

        {/* Hexagonal background with glassmorphism */}
        <div className="w-12 h-12 bg-white/20 dark:bg-slate-800/30 backdrop-blur-md border border-white/30 dark:border-slate-600/40 shadow-lg"
             style={{
               clipPath: 'polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%)'
             }}>
          {/* Subtle gradient overlay based on node type */}
          <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass}`}
               style={{
                 clipPath: 'polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%)'
               }} />
        </div>

        {/* Icon centered */}
        <div className="absolute inset-0 flex items-center justify-center">
          <IconComponent className="w-5 h-5 text-slate-700 dark:text-slate-300" />
        </div>
      </div>

      {/* Label below the hexagon */}
      {label && (
        <div className="mt-2 text-center">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-tight max-w-20">
            {label}
          </p>
        </div>
      )}
    </div>
  );
};

// Node types for React Flow
const nodeTypes = {
  custom: CustomNode,
};

// Flow map data for each agent type - horizontal layout
const flowData = {
  scheduling: {
    nodes: [
      { id: 'start', x: 8, y: 50, label: 'Customer Request', icon: 'whatsapp' },
      { id: 'calendar-check', x: 25, y: 50, label: 'Check Availability', icon: 'calendar' },
      { id: 'time-ask', x: 42, y: 25, label: 'Ask Preferred Time', icon: 'calendar' },
      { id: 'date-ask', x: 42, y: 75, label: 'Ask Preferred Date', icon: 'calendar' },
      { id: 'booking-synkro', x: 58, y: 50, label: 'Synkro Books Appointment', icon: 'synkro', description: 'Auto-booked' },
      { id: 'crm-update', x: 75, y: 50, label: 'Update CRM', icon: 'synkro', description: 'Synced' },
      { id: 'confirmation', x: 92, y: 50, label: 'Send Confirmation', icon: 'whatsapp' }
    ],
    connections: [
      { from: 'start', to: 'calendar-check' },
      { from: 'calendar-check', to: 'time-ask' },
      { from: 'calendar-check', to: 'date-ask' },
      { from: 'time-ask', to: 'booking-synkro' },
      { from: 'date-ask', to: 'booking-synkro' },
      { from: 'booking-synkro', to: 'crm-update' },
      { from: 'crm-update', to: 'confirmation' }
    ]
  },
  customerService: {
    nodes: [
      { id: 'start', x: 8, y: 50, label: 'Customer Issue', icon: 'whatsapp' },
      { id: 'lookup-order', x: 25, y: 50, label: 'Lookup Order History', icon: 'synkro', description: 'Auto-retrieved' },
      { id: 'identify-issue', x: 42, y: 25, label: 'Identify Issue Type', icon: 'message' },
      { id: 'check-stock', x: 42, y: 75, label: 'Check Stock/Inventory', icon: 'shopping' },
      { id: 'process-return', x: 58, y: 15, label: 'Process Return', icon: 'synkro', description: 'Auto-processed' },
      { id: 'schedule-delivery', x: 58, y: 40, label: 'Schedule Replacement', icon: 'calendar' },
      { id: 'update-inventory', x: 58, y: 85, label: 'Update Inventory', icon: 'synkro', description: 'Auto-updated' },
      { id: 'crm-update', x: 75, y: 50, label: 'Update CRM', icon: 'synkro', description: 'Logged' },
      { id: 'send-update', x: 92, y: 50, label: 'Send Customer Update', icon: 'whatsapp' }
    ],
    connections: [
      { from: 'start', to: 'lookup-order' },
      { from: 'lookup-order', to: 'identify-issue' },
      { from: 'lookup-order', to: 'check-stock' },
      { from: 'identify-issue', to: 'process-return' },
      { from: 'identify-issue', to: 'schedule-delivery' },
      { from: 'check-stock', to: 'update-inventory' },
      { from: 'process-return', to: 'crm-update' },
      { from: 'schedule-delivery', to: 'crm-update' },
      { from: 'update-inventory', to: 'crm-update' },
      { from: 'crm-update', to: 'send-update' }
    ]
  },
  orderProcessing: {
    nodes: [
      { id: 'start', x: 8, y: 50, label: 'Order Request', icon: 'whatsapp' },
      { id: 'item-confirmation', x: 25, y: 50, label: 'Confirm Items', icon: 'shopping' },
      { id: 'payment-method', x: 42, y: 25, label: 'Choose Payment', icon: 'credit-card' },
      { id: 'delivery-address', x: 42, y: 75, label: 'Delivery Address', icon: 'message' },
      { id: 'process-payment', x: 58, y: 15, label: 'Process Payment', icon: 'credit-card' },
      { id: 'calculate-delivery', x: 58, y: 40, label: 'Calculate Delivery', icon: 'calendar' },
      { id: 'verify-address', x: 58, y: 85, label: 'Verify Address', icon: 'message' },
      { id: 'create-order', x: 75, y: 50, label: 'Create Order in System', icon: 'synkro', description: 'Auto-created' },
      { id: 'update-crm', x: 92, y: 35, label: 'Update Customer CRM', icon: 'synkro', description: 'Synced' },
      { id: 'send-confirmation', x: 92, y: 65, label: 'Send Order Confirmation', icon: 'whatsapp' }
    ],
    connections: [
      { from: 'start', to: 'item-confirmation' },
      { from: 'item-confirmation', to: 'payment-method' },
      { from: 'item-confirmation', to: 'delivery-address' },
      { from: 'payment-method', to: 'process-payment' },
      { from: 'delivery-address', to: 'calculate-delivery' },
      { from: 'delivery-address', to: 'verify-address' },
      { from: 'process-payment', to: 'create-order' },
      { from: 'calculate-delivery', to: 'create-order' },
      { from: 'verify-address', to: 'create-order' },
      { from: 'create-order', to: 'update-crm' },
      { from: 'create-order', to: 'send-confirmation' }
    ]
  },
  leadQualification: {
    nodes: [
      { id: 'start', x: 8, y: 50, label: 'Lead Inquiry', icon: 'whatsapp' },
      { id: 'initial-questions', x: 25, y: 50, label: 'Initial Questions', icon: 'message' },
      { id: 'budget-check', x: 42, y: 25, label: 'Budget Assessment', icon: 'credit-card' },
      { id: 'timeline-check', x: 42, y: 75, label: 'Timeline Requirements', icon: 'calendar' },
      { id: 'qualify-lead', x: 58, y: 15, label: 'Lead Not Qualified', icon: 'users' },
      { id: 'schedule-call', x: 58, y: 40, label: 'Schedule Discovery Call', icon: 'synkro', description: 'Auto-booked' },
      { id: 'create-proposal', x: 58, y: 85, label: 'Create Custom Proposal', icon: 'message' },
      { id: 'update-crm', x: 75, y: 50, label: 'Update CRM', icon: 'synkro', description: 'Lead created' },
      { id: 'send-calendar', x: 92, y: 35, label: 'Send Calendar Invite', icon: 'calendar' },
      { id: 'send-proposal', x: 92, y: 65, label: 'Send Proposal', icon: 'message' }
    ],
    connections: [
      { from: 'start', to: 'initial-questions' },
      { from: 'initial-questions', to: 'budget-check' },
      { from: 'initial-questions', to: 'timeline-check' },
      { from: 'budget-check', to: 'qualify-lead' },
      { from: 'budget-check', to: 'schedule-call' },
      { from: 'timeline-check', to: 'create-proposal' },
      { from: 'qualify-lead', to: 'update-crm' },
      { from: 'schedule-call', to: 'update-crm' },
      { from: 'create-proposal', to: 'update-crm' },
      { from: 'update-crm', to: 'send-calendar' },
      { from: 'update-crm', to: 'send-proposal' }
    ]
  },
  conversationalSupport: {
    nodes: [
      { id: 'start', x: 8, y: 50, label: 'Customer Message', icon: 'whatsapp' },
      { id: 'analyze-intent', x: 25, y: 50, label: 'Analyze Intent', icon: 'message' },
      { id: 'faq-check', x: 42, y: 25, label: 'Check FAQ Database', icon: 'synkro', description: 'Auto-searched' },
      { id: 'route-department', x: 42, y: 75, label: 'Route to Department', icon: 'users' },
      { id: 'provide-answer', x: 58, y: 15, label: 'Provide Instant Answer', icon: 'message' },
      { id: 'escalate-human', x: 58, y: 40, label: 'Escalate to Human', icon: 'users' },
      { id: 'connect-specialist', x: 58, y: 85, label: 'Connect to Specialist', icon: 'message' },
      { id: 'log-interaction', x: 75, y: 50, label: 'Log Interaction', icon: 'synkro', description: 'Recorded' },
      { id: 'follow-up', x: 92, y: 35, label: 'Schedule Follow-up', icon: 'calendar' },
      { id: 'send-response', x: 92, y: 65, label: 'Send Response', icon: 'whatsapp' }
    ],
    connections: [
      { from: 'start', to: 'analyze-intent' },
      { from: 'analyze-intent', to: 'faq-check' },
      { from: 'analyze-intent', to: 'route-department' },
      { from: 'faq-check', to: 'provide-answer' },
      { from: 'route-department', to: 'escalate-human' },
      { from: 'route-department', to: 'connect-specialist' },
      { from: 'provide-answer', to: 'log-interaction' },
      { from: 'escalate-human', to: 'log-interaction' },
      { from: 'connect-specialist', to: 'log-interaction' },
      { from: 'log-interaction', to: 'follow-up' },
      { from: 'log-interaction', to: 'send-response' }
    ]
  }
};

// Convert flow data to React Flow format
const convertFlowToReactFlow = (flow: any) => {
  const getIconComponent = (iconType: string) => {
    switch (iconType) {
      case 'whatsapp':
        return MessageCircle;
      case 'calendar':
        return Calendar;
      case 'synkro':
        return () => <img src="/synkro.png" alt="Synkro" className="h-6 w-6 dark:filter brightness-0 dark:invert" />;
      case 'message':
        return MessageSquare;
      case 'shopping':
        return ShoppingCart;
      case 'credit-card':
        return CreditCard;
      case 'users':
        return Users;
      default:
        return MessageCircle;
    }
  };

  const getNodeColor = (iconType: string) => {
    switch (iconType) {
      case 'whatsapp':
        return 'from-green-500 to-emerald-600';
      case 'calendar':
        return 'from-blue-500 to-indigo-600';
      case 'synkro':
        return 'from-purple-500 to-violet-600';
      case 'message':
        return 'from-slate-500 to-slate-600';
      case 'shopping':
        return 'from-orange-500 to-red-600';
      case 'credit-card':
        return 'from-cyan-500 to-blue-600';
      case 'users':
        return 'from-pink-500 to-rose-600';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  const nodes = flow.nodes.map((node: any, index: number) => ({
    id: node.id,
    type: 'custom',
    position: {
      x: node.x * 10, // Wider horizontal spacing
      y: node.y * 5 // Slightly increased vertical spacing
    },
    data: {
      icon: getIconComponent(node.icon),
      label: node.label,
      description: node.description,
      color: getNodeColor(node.icon),
    },
  }));

  const edges = flow.connections.map((connection: any, index: number) => ({
    id: `edge-${connection.from}-${connection.to}-${index}`,
    source: connection.from,
    target: connection.to,
    sourceHandle: 'right',
    targetHandle: 'left',
    type: 'smoothstep',
    style: {
      stroke: '#334155', // Dark slate for good visibility
      strokeWidth: 2, // Normal thickness
    },
    markerEnd: {
      type: 'arrowclosed',
      color: '#334155',
      width: 12,
      height: 12,
    },
  }));

  return { nodes, edges };
};

// FlowMap Component using React Flow
const FlowMap = ({ agentType }: { agentType: string }) => {
  const flow = flowData[agentType as keyof typeof flowData];
  const { nodes, edges } = React.useMemo(() => convertFlowToReactFlow(flow), [agentType]);

  const reactFlowRef = React.useRef<any>(null);

  // Auto-fit view when agentType changes
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (reactFlowRef.current) {
        reactFlowRef.current.fitView({
          padding: 0.2,
          includeHiddenNodes: false,
          duration: 200
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [agentType]); // Run on mount and when agentType changes

  return (
    <div className="w-full h-full relative min-h-[300px]">
      <ReactFlow
        key={`${agentType}-${nodes.length}-${edges.length}`}
        ref={reactFlowRef}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: false
        }}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        className="bg-slate-50 dark:bg-slate-900"
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        onWheel={(event) => {
          // Prevent ReactFlow from capturing scroll events - allow page scrolling
          event.stopPropagation();
          event.preventDefault();
        }}
        preventScrolling={false}
        onInit={(instance) => {
          reactFlowRef.current = instance;
        }}
      >
        <Background color="transparent" gap={0} />
        <Controls showZoom={false} showFitView={false} showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

interface IntegrationsFlowMapProps {
  agentType: 'scheduling' | 'customerService' | 'orderProcessing' | 'leadQualification' | 'conversationalSupport';
  height?: string;
}

const IntegrationsFlowMap: React.FC<IntegrationsFlowMapProps> = ({
  agentType,
  height = '24rem'
}) => {
  return (
    <div
      className={`bg-slate-50 dark:bg-slate-900 rounded-b-2xl border-x border-b border-slate-200 dark:border-slate-700 p-4`}
      style={{ height }}
      onWheel={(e) => {
        // Allow normal page scrolling when mouse is over the flow map
        e.stopPropagation();
      }}
    >
      <FlowMap agentType={agentType} />
    </div>
  );
};

export default IntegrationsFlowMap;
